// Education-specific comparison utility functions for the ComparisonView

export { getComparisonTargets } from './comparisonUtils'

// Infrastructure comparison functions
import {
  calculateResourceRatios,
  calculateInfrastructureScores,
  calculateWASHCompliance,
} from './educationChartUtils'

/**
 * Groups education institutions by location for comparison analysis
 * @param {Array} institutions - Array of education institutions
 * @param {Array} targets - Array of comparison target locations
 * @param {string} level - Location level ('district', 'subcounty', 'parish')
 * @returns {Array} Grouped institutions by location
 */
export function groupEducationInstitutionsByLocation(
  institutions,
  targets,
  level
) {
  const grouped = []

  targets.forEach(target => {
    const locationInstitutions = institutions.filter(institution => {
      switch (level) {
        case 'district':
          return (
            institution.district?.toLowerCase().trim() ===
            target.name?.toLowerCase().trim()
          )
        case 'subcounty':
          return (
            institution.subcounty?.toLowerCase().trim() ===
            target.name?.toLowerCase().trim()
          )
        case 'parish':
          return (
            institution.parish?.toLowerCase().trim() ===
            target.name?.toLowerCase().trim()
          )
        case 'village': {
          // Handle empty village fields - only match if both are non-empty
          const institutionVillage = institution.village?.toLowerCase().trim()
          const targetVillage = target.name?.toLowerCase().trim()
          return (
            institutionVillage &&
            targetVillage &&
            institutionVillage === targetVillage
          )
        }
        default:
          return false
      }
    })

    if (locationInstitutions.length > 0) {
      grouped.push({
        id: target.id,
        name: target.name,
        code: target.code,
        institutions: locationInstitutions,
        level,
      })
    }
  })

  return grouped
}

/**
 * Calculates comprehensive education statistics for location groups
 * @param {Array} locationGroups - Grouped institutions by location
 * @returns {Array} Education statistics for each location
 */
export function calculateEducationLocationStats(locationGroups) {
  return locationGroups.map(location => {
    const institutions = location.institutions
    const totalInstitutions = institutions.length

    // Count by institution type
    const primaryCount = institutions.filter(
      inst => inst.institution_type?.toLowerCase() === 'primary'
    ).length
    const secondaryCount = institutions.filter(
      inst => inst.institution_type?.toLowerCase() === 'secondary'
    ).length

    // Count by ownership
    const governmentCount = institutions.filter(
      inst => inst.ownership?.toLowerCase() === 'government'
    ).length
    const privateCount = institutions.filter(
      inst => inst.ownership?.toLowerCase() === 'private'
    ).length

    // Calculate ratios and percentages
    const governmentRatio =
      totalInstitutions > 0
        ? Math.round((governmentCount / totalInstitutions) * 100)
        : 0

    const privateRatio =
      totalInstitutions > 0
        ? Math.round((privateCount / totalInstitutions) * 100)
        : 0

    const primaryToSecondaryRatio =
      secondaryCount > 0
        ? Math.round((primaryCount / secondaryCount) * 10) / 10
        : primaryCount > 0
          ? 99
          : 0

    // Calculate parish coverage (unique parishes with institutions)
    const uniqueParishes = new Set(
      institutions.map(inst => inst.parish).filter(parish => parish)
    )
    const parishCoverage = uniqueParishes.size

    // Calculate student enrollment metrics
    const totalStudents = institutions.reduce(
      (sum, inst) => sum + (parseFloat(inst.total_learners) || 0),
      0
    )
    const totalTeachers = institutions.reduce(
      (sum, inst) => sum + (parseFloat(inst.total_teachers) || 0),
      0
    )

    // Calculate average student-teacher ratio for this location
    const avgStudentTeacherRatio =
      totalTeachers > 0 ? totalStudents / totalTeachers : 0

    // Calculate infrastructure readiness percentage
    const institutionsWithData = institutions.filter(
      inst =>
        inst.electricity_available !== undefined &&
        inst.water_available !== undefined
    )
    const institutionsWithInfrastructure = institutions.filter(
      inst =>
        inst.electricity_available === 'Yes' && inst.water_available === 'Yes'
    )
    const infrastructurePercentage =
      institutionsWithData.length > 0
        ? Math.round(
            (institutionsWithInfrastructure.length /
              institutionsWithData.length) *
              100
          )
        : 0

    // Calculate Primary School Quality Score
    // Weighted: enrollment scale (30%), resource adequacy (25%), infrastructure (25%), government support (20%)
    const enrollmentScore = Math.min((totalStudents / 1000) * 100, 100) // Max score at 1000+ students
    const resourceScore =
      avgStudentTeacherRatio > 0
        ? Math.max(0, 100 - (avgStudentTeacherRatio - 30) * 2) // Optimal at 30:1, penalty beyond that
        : 0
    const infrastructureScore = infrastructurePercentage // Already a percentage
    const governmentScore = governmentRatio // Already a percentage

    const qualityScore = Math.round(
      enrollmentScore * 0.3 +
        resourceScore * 0.25 +
        infrastructureScore * 0.25 +
        governmentScore * 0.2
    )

    // Keep old compositeScore for backward compatibility
    const institutionScore = Math.min((totalInstitutions / 10) * 100, 100) // Max score at 10+ institutions
    const secondaryScore = secondaryCount > 0 ? 100 : 0 // Binary: has secondary or not
    const coverageScore = Math.min((parishCoverage / 5) * 100, 100) // Max score at 5+ parishes

    const compositeScore = Math.round(
      institutionScore * 0.4 +
        governmentScore * 0.3 +
        secondaryScore * 0.2 +
        coverageScore * 0.1
    )

    return {
      id: location.id,
      name: location.name,
      code: location.code,
      totalInstitutions,
      primaryCount,
      secondaryCount,
      governmentCount,
      privateCount,
      governmentRatio,
      privateRatio,
      primaryToSecondaryRatio,
      parishCoverage,
      compositeScore,
      // New primary school quality metrics
      totalStudents,
      totalTeachers,
      avgStudentTeacherRatio,
      infrastructurePercentage,
      qualityScore,
      institutions, // Keep reference for detailed analysis
    }
  })
}

/**
 * Creates a comparison chart showing public vs private education ratios
 * @param {Array} locationStats - Education statistics by location
 * @param {string} title - Chart title
 * @returns {Object} ECharts option for public/private ratio comparison
 */
export function createPublicPrivateRatioComparison(locationStats, title) {
  const locations = locationStats.map(stat => stat.name)
  const governmentData = locationStats.map(stat => stat.governmentRatio)
  const privateData = locationStats.map(stat => stat.privateRatio)

  return {
    title: {
      show: false,
      text: title,
      left: 'center',
      textStyle: { fontSize: 16, fontWeight: 'bold' },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    legend: {
      data: ['Government', 'Private'],
      top: '0',
      itemStyle: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      textStyle: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: 0,
      top: '12%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: locations,
      axisLabel: {
        rotate: locations.length > 5 ? 45 : 0,
        interval: 0,
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    yAxis: {
      type: 'value',
      name: 'Percentage (%)',
      max: 100,
      axisLabel: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    series: [
      {
        name: 'Government',
        type: 'bar',
        stack: 'ownership',
        data: governmentData,
        itemStyle: { color: '#ef233c' },
        label: {
          show: true,
          position: 'inside',
          formatter: '{c}%',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      {
        name: 'Private',
        type: 'bar',
        stack: 'ownership',
        data: privateData,
        itemStyle: { color: '#c10e66' },
        label: {
          show: true,
          position: 'inside',
          formatter: '{c}%',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
    ],
  }
}

/**
 * Creates a bubble chart for educational equity analysis
 * @param {Array} locationStats - Education statistics by location
 * @param {string} title - Chart title
 * @returns {Object} ECharts option for educational equity bubble chart
 */
export function createEducationalEquityBubbleChart(locationStats, title) {
  const data = locationStats.map(stat => ({
    name: stat.name,
    value: [
      stat.totalInstitutions, // x-axis: total institutions
      stat.governmentRatio, // y-axis: government percentage
      stat.parishCoverage * 10, // bubble size: parish coverage * 10
      stat.compositeScore, // color intensity
    ],
  }))

  return {
    title: {
      show: false,
      text: title,
      left: 'center',
      textStyle: { fontSize: 16, fontWeight: 'bold' },
    },
    tooltip: {
      formatter: params => {
        const data = params.data
        return `<b>${data.name}</b><br/>Institutions: ${data.value[0]}<br/>Government: ${data.value[1]}%<br/>Parish Coverage: ${data.value[2] / 10}<br/>Score: ${data.value[3]}`
      },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    grid: {
      left: '10%',
      right: '15%',
      bottom: '15%',
      top: '10%',
    },
    xAxis: {
      type: 'value',
      name: 'Total Institutions',
      nameLocation: 'middle',
      nameGap: 30,
      axisLabel: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'Government Ratio (%)',
      nameLocation: 'middle',
      nameGap: 50,
      max: 100,
      axisLabel: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    visualMap: {
      min: Math.min(...locationStats.map(stat => stat.compositeScore)),
      max: Math.max(...locationStats.map(stat => stat.compositeScore)),
      dimension: 3,
      orient: 'vertical',
      right: '5%',
      top: 'middle',
      text: ['High Score', 'Low Score'],
      calculable: true,
      inRange: {
        color: ['#fce4ec', '#ef233c'],
      },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    series: [
      {
        name: 'Education Equity',
        type: 'scatter',
        data: data,
        symbolSize: params => {
          if (
            params &&
            params.data &&
            params.data.value &&
            params.data.value[2] !== undefined
          ) {
            return Math.max(params.data.value[2], 20)
          }
          return 20
        },
        label: {
          show: false,
          position: 'top',
          formatter: '{b}',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
    ],
  }
}

// ========================================
// INFRASTRUCTURE COMPARISON FUNCTIONS
// ========================================

/**
 * Calculate infrastructure and capacity metrics by sub-location for comparison
 * @param {Array} grouped - Array of grouped institutions by location
 * @returns {Array} Infrastructure stats by location
 */
export function calculateInfrastructureLocationStats(grouped) {
  return grouped.map(location => {
    const { institutions } = location

    if (!institutions.length) {
      return {
        ...location,
        totalInstitutions: 0,
        infrastructureScore: 0,
        resourceRatios: null,
        washCompliance: null,
        teacherWorkforceQuality: null,
        avgPupilTeacherRatio: 0,
        avgPupilClassroomRatio: 0,
        avgPupilToiletRatio: 0,
        femaleTeacherPercentage: 0,
        disabilityAccessibility: 0,
        governmentPayrollPercentage: 0,
      }
    }

    // Calculate aggregate resource ratios for the location
    const locationResourceRatios = calculateResourceRatios(institutions)

    // Calculate infrastructure completeness scores for each school
    const infrastructureScores = calculateInfrastructureScores(institutions)
    const avgInfrastructureScore =
      infrastructureScores.length > 0
        ? Math.round(
            infrastructureScores.reduce(
              (sum, school) => sum + school.score,
              0
            ) / infrastructureScores.length
          )
        : 0

    // Calculate WASH compliance
    const washCompliance = calculateWASHCompliance(institutions)

    // Calculate teacher workforce metrics
    const totalTeachers = institutions.reduce(
      (sum, inst) => sum + (parseFloat(inst.total_teachers) || 0),
      0
    )
    const femaleTeachers = institutions.reduce(
      (sum, inst) => sum + (parseFloat(inst.female_teachers) || 0),
      0
    )
    const degreeTeachers = institutions.reduce(
      (sum, inst) => sum + (parseFloat(inst.degree_teachers) || 0),
      0
    )
    const diplomaTeachers = institutions.reduce(
      (sum, inst) => sum + (parseFloat(inst.diploma_teachers) || 0),
      0
    )
    const certificateTeachers = institutions.reduce(
      (sum, inst) => sum + (parseFloat(inst.certificate_teachers) || 0),
      0
    )
    const govPayrollTeachers = institutions.reduce(
      (sum, inst) => sum + (parseFloat(inst.govt_payroll_teachers) || 0),
      0
    )

    // Calculate disability accessibility
    const schoolsWithDisabilityAccess = institutions.filter(
      inst => inst.disability_accessible_toilets === 'Yes'
    ).length
    const disabilityAccessibility =
      institutions.length > 0
        ? Math.round((schoolsWithDisabilityAccess / institutions.length) * 100)
        : 0

    return {
      ...location,
      totalInstitutions: institutions.length,
      infrastructureScore: avgInfrastructureScore,
      resourceRatios: locationResourceRatios,
      washCompliance,
      avgPupilTeacherRatio: locationResourceRatios?.pupilTeacherRatio || 0,
      avgPupilClassroomRatio: locationResourceRatios?.pupilClassroomRatio || 0,
      avgPupilToiletRatio: locationResourceRatios?.pupilToiletRatio || 0,
      femaleTeacherPercentage:
        totalTeachers > 0
          ? Math.round((femaleTeachers / totalTeachers) * 100)
          : 0,
      teacherWorkforceQuality: {
        total: totalTeachers,
        degree: degreeTeachers,
        diploma: diplomaTeachers,
        certificate: certificateTeachers,
        degreePercentage:
          totalTeachers > 0
            ? Math.round((degreeTeachers / totalTeachers) * 100)
            : 0,
        diplomaPercentage:
          totalTeachers > 0
            ? Math.round((diplomaTeachers / totalTeachers) * 100)
            : 0,
        certificatePercentage:
          totalTeachers > 0
            ? Math.round((certificateTeachers / totalTeachers) * 100)
            : 0,
      },
      disabilityAccessibility,
      governmentPayrollPercentage:
        totalTeachers > 0
          ? Math.round((govPayrollTeachers / totalTeachers) * 100)
          : 0,
    }
  })
}

/**
 * Create Infrastructure Completeness Ranking chart
 * @param {Array} locationStats - Infrastructure stats by location
 * @param {string} title - Chart title
 * @returns {Object} ECharts option
 */
export function createInfrastructureCompletenessRankingOption(
  locationStats,
  title
) {
  if (!locationStats.length) return null

  // Sort by infrastructure score (descending)
  const sortedStats = [...locationStats].sort(
    (a, b) => b.infrastructureScore - a.infrastructureScore
  )

  return {
    title: {
      show: false,
      text: title,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        const data = params[0]
        const location = sortedStats[data.dataIndex]
        const status =
          location.infrastructureScore >= 75
            ? 'Excellent'
            : location.infrastructureScore >= 50
              ? 'Good'
              : location.infrastructureScore >= 25
                ? 'Needs Improvement'
                : 'Critical'
        return `
          <div style="font-family: Poppins, sans-serif;">
            <strong>${location.name}</strong><br/>
            Infrastructure Score: ${location.infrastructureScore}%<br/>
            Schools: ${location.totalInstitutions}<br/>
            Status: <span style="color: ${
              location.infrastructureScore >= 75
                ? '#10B981'
                : location.infrastructureScore >= 50
                  ? '#3B82F6'
                  : location.infrastructureScore >= 25
                    ? '#F59E0B'
                    : '#EF4444'
            };">${status}</span>
          </div>
        `
      },
    },
    grid: {
      left: '3%',
      right: '10%',
      bottom: '5%',
      top: '2%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      name: 'Infrastructure Completeness (%)',
      nameLocation: 'middle',
      nameGap: 25,
      max: 100,
      nameTextStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
      axisLabel: {
        fontFamily: 'Poppins, sans-serif',
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'category',
      data: sortedStats.map(stat => stat.name),
      axisLabel: {
        fontFamily: 'Poppins, sans-serif',
      },
    },
    series: [
      {
        type: 'bar',
        data: sortedStats.map(stat => ({
          value: stat.infrastructureScore,
          itemStyle: {
            color:
              stat.infrastructureScore >= 75
                ? '#10B981'
                : stat.infrastructureScore >= 50
                  ? '#3B82F6'
                  : stat.infrastructureScore >= 25
                    ? '#F59E0B'
                    : '#EF4444',
          },
        })),
        label: {
          show: true,
          position: 'right',
          formatter: '{c}%',
          fontFamily: 'Poppins, sans-serif',
        },
      },
    ],
  }
}

/**
 * Create Resource Adequacy Comparison chart (grouped bars)
 * @param {Array} locationStats - Infrastructure stats by location
 * @param {string} title - Chart title
 * @returns {Object} ECharts option
 */
export function createResourceAdequacyComparisonOption(locationStats, title) {
  if (!locationStats.length) return null

  const locations = locationStats
    .filter(stat => stat.resourceRatios)
    .slice(0, 10) // Top 10 locations

  return {
    title: {
      show: false,
      text: title,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        if (!params.length) return ''
        const location = locations[params[0].dataIndex]
        return `
          <div style="font-family: Poppins, sans-serif;">
            <strong>${location.name}</strong><br/>
            Pupil-Teacher: ${location.avgPupilTeacherRatio.toFixed(1)}:1<br/>
            Pupil-Classroom: ${location.avgPupilClassroomRatio.toFixed(1)}:1<br/>
            Pupil-Toilet: ${location.avgPupilToiletRatio.toFixed(1)}:1<br/>
            Schools: ${location.totalInstitutions}
          </div>
        `
      },
    },
    legend: {
      data: [
        'Pupil-Teacher Ratio',
        'Pupil-Classroom Ratio',
        'Pupil-Toilet Ratio',
      ],
      top: '0',
      textStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
    },
    grid: {
      left: '3%',
      right: '10%',
      bottom: '3%',
      top: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: locations.map(stat => stat.name),
      axisLabel: {
        interval: 0,
        rotate: 45,
        fontSize: 10,
        fontFamily: 'Poppins, sans-serif',
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'Ratio',
      nameTextStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
      axisLabel: {
        fontFamily: 'Poppins, sans-serif',
      },
      splitLine: { show: false },
    },
    series: [
      {
        name: 'Pupil-Teacher Ratio',
        type: 'bar',
        barGap: 0,
        data: locations.map(stat => stat.avgPupilTeacherRatio),
        itemStyle: { color: '#3B82F6' },
        markLine: {
          data: [
            {
              yAxis: 40,
              lineStyle: { color: '#DC2626', type: 'dashed' },
              label: { formatter: 'UNESCO Primary (40:1)' },
            },
          ],
        },
      },
      {
        name: 'Pupil-Classroom Ratio',
        type: 'bar',
        barGap: 0,
        data: locations.map(stat => stat.avgPupilClassroomRatio),
        itemStyle: { color: '#10B981' },
        markLine: {
          data: [
            {
              yAxis: 40,
              lineStyle: { color: '#DC2626', type: 'dashed' },
              label: { formatter: 'Target (40:1)' },
            },
          ],
        },
      },
      {
        name: 'Pupil-Toilet Ratio',
        type: 'bar',
        data: locations.map(stat => stat.avgPupilToiletRatio),
        itemStyle: { color: '#F59E0B' },
        markLine: {
          data: [
            {
              yAxis: 30,
              lineStyle: { color: '#DC2626', type: 'dashed' },
              label: { formatter: 'Target (30:1)' },
            },
          ],
        },
      },
    ],
  }
}

/**
 * Create WASH Compliance Comparison chart
 * @param {Array} locationStats - Infrastructure stats by location
 * @param {string} title - Chart title
 * @returns {Object} ECharts option
 */
export function createWASHComplianceComparisonOption(locationStats, title) {
  if (!locationStats.length) return null

  const locations = locationStats
    .filter(stat => stat.washCompliance)
    .slice(0, 10)

  return {
    title: {
      show: false,
      text: title,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        if (!params.length) return ''
        const location = locations[params[0].dataIndex]
        const wash = location.washCompliance
        return `
          <div style="font-family: Poppins, sans-serif;">
            <strong>${location.name}</strong><br/>
            Water Access: ${wash.water.percentage}%<br/>
            Handwashing: ${wash.handwashing.percentage}%<br/>
            Gender Toilets: ${wash.genderToilets.percentage}%<br/>
            Schools: ${location.totalInstitutions}
          </div>
        `
      },
    },
    legend: {
      data: [
        'Water Access',
        'Handwashing Facilities',
        'Gender-Separated Toilets',
      ],
      top: '0',
      textStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: locations.map(stat => stat.name),
      axisLabel: {
        interval: 0,
        rotate: 45,
        fontSize: 10,
        fontFamily: 'Poppins, sans-serif',
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'Coverage (%)',
      max: 100,
      nameTextStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
      axisLabel: {
        fontFamily: 'Poppins, sans-serif',
      },
      splitLine: { show: false },
    },
    series: [
      {
        name: 'Water Access',
        type: 'bar',
        barGap: 0,
        data: locations.map(stat => stat.washCompliance.water.percentage),
        itemStyle: { color: '#3B82F6' },
      },
      {
        name: 'Handwashing Facilities',
        type: 'bar',
        data: locations.map(stat => stat.washCompliance.handwashing.percentage),
        itemStyle: { color: '#10B981' },
      },
      {
        name: 'Gender-Separated Toilets',
        type: 'bar',
        data: locations.map(
          stat => stat.washCompliance.genderToilets.percentage
        ),
        itemStyle: { color: '#F59E0B' },
      },
    ],
    markLine: {
      data: [
        {
          yAxis: 80,
          lineStyle: { color: '#DC2626', type: 'dashed' },
          label: { formatter: 'Target: 80%' },
        },
      ],
    },
  }
}

/**
 * Create Teacher Workforce Quality comparison chart
 * @param {Array} locationStats - Infrastructure stats by location
 * @param {string} title - Chart title
 * @returns {Object} ECharts option
 */
export function createTeacherWorkforceQualityOption(locationStats, title) {
  if (!locationStats.length) return null

  const locations = locationStats
    .filter(
      stat =>
        stat.teacherWorkforceQuality && stat.teacherWorkforceQuality.total > 0
    )
    .slice(0, 10)

  return {
    title: {
      show: false,
      text: title,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        if (!params.length) return ''
        const location = locations[params[0].dataIndex]
        const workforce = location.teacherWorkforceQuality
        return `
          <div style="font-family: Poppins, sans-serif;">
            <strong>${location.name}</strong><br/>
            Total Teachers: ${workforce.total}<br/>
            Degree: ${workforce.degreePercentage}%<br/>
            Diploma: ${workforce.diplomaPercentage}%<br/>
            Certificate: ${workforce.certificatePercentage}%
          </div>
        `
      },
    },
    legend: {
      data: ['Certificate', 'Diploma', 'Degree'],
      top: '0',
      textStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: locations.map(stat => stat.name),
      axisLabel: {
        interval: 0,
        rotate: 45,
        fontSize: 10,
        fontFamily: 'Poppins, sans-serif',
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'Percentage (%)',
      max: 100,
      nameTextStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
      axisLabel: {
        fontFamily: 'Poppins, sans-serif',
      },
      splitLine: { show: false },
    },
    series: [
      {
        name: 'Certificate',
        type: 'bar',
        stack: 'qualification',
        data: locations.map(
          stat => stat.teacherWorkforceQuality.certificatePercentage
        ),
        itemStyle: { color: '#EF4444' },
      },
      {
        name: 'Diploma',
        type: 'bar',
        stack: 'qualification',
        data: locations.map(
          stat => stat.teacherWorkforceQuality.diplomaPercentage
        ),
        itemStyle: { color: '#F59E0B' },
      },
      {
        name: 'Degree',
        type: 'bar',
        stack: 'qualification',
        data: locations.map(
          stat => stat.teacherWorkforceQuality.degreePercentage
        ),
        itemStyle: { color: '#10B981' },
      },
    ],
  }
}
